import React, { useRef, useState } from 'react';
import { Box, Stack, Text } from '@mantine/core';
import { AfaCommentBox } from '../AfaCommentBox/AfaCommentBox';

export interface Comment {
  /** Nombre del autor del comentario */
  userName: string;
  /** Fecha de creación del comentario */
  commentDate: string;
  /** Contenido del comentario */
  commentText: string;
  isAFA: boolean;
}

export interface AfaCommentHistoryProps {
  comments: Comment[];
  maxHeight?: string | number;
  minHeight?: string | number;
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  emptyMessage?: string;
  style?: React.CSSProperties;
}

export const AfaCommentHistory = ({
  comments,
  maxHeight = '4000px',
  minHeight = '50px',
  gap = 'md',
  emptyMessage = 'No hi ha comentaris disponibles',
  style,
}: AfaCommentHistoryProps) => {
  const [height, setHeight] = useState(100);
  const [isResizing, setIsResizing] = useState(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const minHeightNum = typeof minHeight === 'number' ? minHeight : parseInt(minHeight) || 100;
  const maxHeightNum = typeof maxHeight === 'number' ? maxHeight : parseInt(maxHeight) || 600;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = e.clientY - startYRef.current;
      const newHeight = startHeightRef.current + deltaY;

      // Limitar entre minHeight y maxHeight
      const clampedHeight = Math.min(Math.max(newHeight, minHeightNum), maxHeightNum);
      setHeight(clampedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minHeightNum, maxHeightNum]);

  return (
    <Box
      bg="brand.0"
      p="md"
      style={{
        borderRadius: '8px',
        position: 'relative',
        userSelect: isResizing ? 'none' : 'auto',
        ...style,
      }}
    >
      {comments.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">
          {emptyMessage}
        </Text>
      ) : (
        <Stack
          gap={gap}
          style={{
            height: `${height}px`,
            overflowY: 'auto',
          }}
        >
          {comments.sort((a, b) => {
            return new Date(b.commentDate).getTime() -
              new Date(a.commentDate).getTime()
          }).map((comment, index) => (
            <AfaCommentBox
              key={index}
              author={comment.isAFA ? "Comentari AFA" : comment.userName}
              date={new Date(comment.commentDate).toLocaleString('ca-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
              content={comment.commentText}
            />
          ))}
        </Stack>
      )}

      {/* Controlador de resize */}
      <Box
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '20px',
          height: '20px',
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #999 50%)',
          borderBottomRightRadius: '8px',
          opacity: 0.5,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          if (!isResizing) e.currentTarget.style.opacity = '0.5';
        }}
      />
    </Box>
  );
};

export default AfaCommentHistory;
